import React, { useState, ChangeEvent, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { gameService } from './firebase/gameService';
import { HostDashboard } from './components/HostDashboard';
import { GameBoard } from './components/GameBoard';
import AudienceView from './components/AudienceView';
import type { Player, PlayerRole } from './types/game';
import HostSetupScreen from './components/HostSetupScreen';
import AdminDashboard from './pages/AdminDashboard';
import AdminAuth from './components/AdminAuth';

const AVATAR_IMAGES = [
  'avatar2.svg',
  'avatar3.svg',
  'avatar4.svg',
  'avatar5.svg',
  'avatar6.svg',
  'avatar7.svg',
  'avatar8.svg',
  'avatar9.svg',
  'avatar10.svg',
];

function App() {
  const [gameId, setGameId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [view, setView] = useState<'host-setup' | 'host' | 'player' | 'audience' | null>(null);
  const [gameCode, setGameCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [hostPassword, setHostPassword] = useState('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [audienceStep, setAudienceStep] = useState<null | 'select' | 'play-along' | 'just-watch'>(null);

  // Prefill game code from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      setGameCode(code.toUpperCase());
      setAudienceStep('play-along');
    }
  }, []);

  const handleCreateGame = async () => {
    if (!hostPassword) {
      alert('Please enter a password for the host controls.');
      return;
    }
    try {
      const game = await gameService.createGame('host-id', hostPassword);
      setGameId(game.id);
      setGameCode(game.code);
      setPlayers([]);
      setView('host-setup');
      setHostPassword('');
    } catch (error) {
      console.error('Error creating game:', error);
      alert('Failed to create game. See console for details.');
    }
  };

  const handleAddPlayer = (name: string) => {
    // Get avatars already in use
    const usedAvatars = players.map(p => p.avatar);
    // Find available avatars
    const availableAvatars = AVATAR_IMAGES.filter(a => !usedAvatars.includes(a));
    if (availableAvatars.length === 0) {
      alert('No more avatars available!');
      return;
    }
    // Pick a random available avatar
    const avatar = availableAvatars[Math.floor(Math.random() * availableAvatars.length)];
    const newPlayer: Player = {
      id: Math.random().toString(36).substring(7),
      name,
      score: 0,
      role: 'player' as PlayerRole,
      avatar,
    };
    setPlayers(prev => [...prev, newPlayer]);
  };

  const handleRemovePlayer = (id: string) => {
    setPlayers(prev => prev.filter(p => p.id !== id));
  };

  const handleStartGame = async () => {
    if (!gameId) return;
    await gameService.updateGame(gameId, { players });
    setView('host');
  };

  const handleBack = () => {
    setGameId(null);
    setGameCode('');
    setPlayers([]);
    setView(null);
  };

  const handleJoinGame = async () => {
    const game = await gameService.getGameByCode(gameCode);
    if (!game) {
      alert('Game not found');
      return;
    }

    const newPlayer: Player = {
      id: Math.random().toString(36).substring(7),
      name: playerName,
      score: 0,
      role: 'player' as PlayerRole,
      avatar: 'avatar2.svg', // fallback/default avatar for join as player
    };

    await gameService.addPlayer(game.id, newPlayer);
    setGameId(game.id);
    setPlayerId(newPlayer.id);
    setView('player');
  };

  const handleJoinAsAudience = () => {
    setAudienceStep('select');
  };

  const handleAudiencePlayAlong = () => {
    setAudienceStep('play-along');
    setPlayerName('');
    setGameCode('');
  };

  const handleAudienceJustWatch = () => {
    setAudienceStep('just-watch');
    setPlayerName('');
    setGameCode('');
  };

  const handleAudienceJoinPlayAlong = async () => {
    const game = await gameService.getGameByCode(gameCode);
    if (!game) {
      alert('Game not found');
      return;
    }
    const newPlayer: Player = {
      id: Math.random().toString(36).substring(7),
      name: playerName,
      score: 0,
      role: 'audience' as PlayerRole,
      avatar: 'avatar2.svg',
    };
    await gameService.addPlayer(game.id, newPlayer);
    setGameId(game.id);
    setPlayerId(newPlayer.id);
    setView('audience');
    setAudienceStep(null);
  };

  const handleAudienceJoinJustWatch = async () => {
    const game = await gameService.getGameByCode(gameCode);
    if (!game) {
      alert('Game not found');
      return;
    }
    setGameId(game.id);
    setView('audience');
    setAudienceStep(null);
  };

  const handleAudienceBack = () => {
    if (audienceStep === 'select') {
      setAudienceStep(null);
    } else {
      setAudienceStep('select');
    }
    setPlayerName('');
    setGameCode('');
  };

  // Router integration
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={
          <AdminAuth>
            <AdminDashboard />
          </AdminAuth>
        } />
        <Route path="/*" element={
          audienceStep === 'select' ? (
            <div className="min-h-screen bg-black flex items-center justify-center p-4">
              <div className="bg-[#232323] p-10 rounded-2xl shadow-2xl border-2 border-brand-yellow/30 max-w-md w-full flex flex-col items-center">
                <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-20 mb-10" />
                <img src="/images/default-avatar.png" alt="Vexl Avatar" className="h-24 w-24 rounded-full mb-8 shadow-lg border-4 border-brand-yellow object-cover" />
                <div className="w-full flex flex-col gap-8">
                  <div>
                    <h2 className="text-2xl font-extrabold mb-5 text-brand-yellow flex items-center gap-2 justify-center tracking-wide">
                      <img src="/images/white-glasses.svg" alt="Join Game" className="h-7" />
                      Join Game
                    </h2>
                    <h3 className="text-xl font-bold text-brand-yellow mb-2 text-center">Audience Mode</h3>
                    <p className="text-white text-center mb-6">Choose how you want to participate</p>
                    <button
                      onClick={handleAudiencePlayAlong}
                      className="w-full bg-brand-yellow text-black font-extrabold text-xl py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors shadow-lg mb-4"
                    >
                      Play Along
                    </button>
                    <button
                      onClick={handleAudienceJustWatch}
                      className="w-full bg-[#5d7763] text-white font-extrabold text-xl py-4 rounded-xl hover:bg-[#6e8b7a]/90 transition-colors shadow-lg mb-4"
                    >
                      Just Watch
                    </button>
                    <button
                      onClick={handleAudienceBack}
                      className="w-full bg-[#2d334a] text-white font-extrabold text-xl py-4 rounded-xl hover:bg-[#3a4260]/90 transition-colors shadow-lg"
                    >
                      Back
                    </button>
                  </div>
                </div>
                <hr className="border-brand-yellow/20 my-8 w-full" />
                <div className="text-center mt-2 w-full">
                  <Link to="/admin" className="text-brand-yellow hover:underline text-lg font-semibold">Admin Panel</Link>
                </div>
              </div>
            </div>
          ) : audienceStep === 'play-along' ? (
            <div className="min-h-screen bg-black flex items-center justify-center p-4">
              <div className="bg-[#232323] p-10 rounded-2xl shadow-2xl border-2 border-brand-yellow/30 max-w-md w-full flex flex-col items-center">
                <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-20 mb-10" />
                <img src="/images/default-avatar.png" alt="Vexl Avatar" className="h-24 w-24 rounded-full mb-8 shadow-lg border-4 border-brand-yellow object-cover" />
                <div className="w-full flex flex-col gap-8">
                  <div>
                    <h2 className="text-2xl font-extrabold mb-5 text-brand-yellow flex items-center gap-2 justify-center tracking-wide">
                      <img src="/images/white-glasses.svg" alt="Join Game" className="h-7" />
                      Play Along
                    </h2>
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      className="w-full bg-black text-white text-lg px-5 py-4 rounded-xl border-2 border-brand-yellow/40 focus:border-brand-yellow focus:outline-none mb-5 shadow-inner"
                    />
                    <input
                      type="text"
                      placeholder="Game Code"
                      value={gameCode}
                      onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                      className="w-full bg-black text-white text-lg px-5 py-4 rounded-xl border-2 border-brand-yellow/40 focus:border-brand-yellow focus:outline-none mb-5 shadow-inner"
                    />
                    <button
                      onClick={handleAudienceJoinPlayAlong}
                      className="w-full bg-brand-yellow text-black font-extrabold text-xl py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors shadow-lg mb-4"
                      disabled={!playerName || !gameCode}
                    >
                      Join Game
                    </button>
                    <button
                      onClick={handleAudienceBack}
                      className="w-full bg-[#2d334a] text-white font-extrabold text-xl py-4 rounded-xl hover:bg-[#3a4260]/90 transition-colors shadow-lg"
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : audienceStep === 'just-watch' ? (
            <div className="min-h-screen bg-black flex items-center justify-center p-4">
              <div className="bg-[#232323] p-10 rounded-2xl shadow-2xl border-2 border-brand-yellow/30 max-w-md w-full flex flex-col items-center">
                <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-20 mb-10" />
                <img src="/images/default-avatar.png" alt="Vexl Avatar" className="h-24 w-24 rounded-full mb-8 shadow-lg border-4 border-brand-yellow object-cover" />
                <div className="w-full flex flex-col gap-8">
                  <div>
                    <h2 className="text-2xl font-extrabold mb-5 text-brand-yellow flex items-center gap-2 justify-center tracking-wide">
                      <img src="/images/white-glasses.svg" alt="Join Game" className="h-7" />
                      Just Watch
                    </h2>
                    <input
                      type="text"
                      placeholder="Game Code"
                      value={gameCode}
                      onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                      className="w-full bg-black text-white text-lg px-5 py-4 rounded-xl border-2 border-brand-yellow/40 focus:border-brand-yellow focus:outline-none mb-5 shadow-inner"
                    />
                    <button
                      onClick={handleAudienceJoinJustWatch}
                      className="w-full bg-brand-yellow text-black font-extrabold text-xl py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors shadow-lg mb-4"
                      disabled={!gameCode}
                    >
                      Join Game
                    </button>
                    <button
                      onClick={handleAudienceBack}
                      className="w-full bg-[#2d334a] text-white font-extrabold text-xl py-4 rounded-xl hover:bg-[#3a4260]/90 transition-colors shadow-lg"
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : !gameId || !view ? (
            <div className="min-h-screen bg-black flex items-center justify-center p-4">
              <div className="bg-black p-10 rounded-2xl shadow-2xl border-2 border-brand-yellow/30 max-w-md w-full flex flex-col items-center">
                <img src="/images/secondary-logo-white.svg" alt="Vexl Logo" className="h-20 mb-4" />
                <img src="/images/default-avatar.png" alt="Vexl Avatar" className="h-24 w-24 mb-8 shadow-lg" />
                <div className="w-full flex flex-col gap-8">
                  {/* Host Section */}
                  <div>
                    <input
                      type="password"
                      placeholder="Host Password (for host controls)"
                      value={hostPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setHostPassword(e.target.value)}
                      className="w-full bg-black text-white text-lg px-5 py-4 rounded-xl border-2 border-brand-yellow/40 focus:border-brand-yellow focus:outline-none mb-5 shadow-inner"
                    />
                    <button
                      onClick={handleCreateGame}
                      className="w-full bg-brand-yellow text-black font-extrabold text-xl py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors shadow-lg"
                    >
                      Create New Game
                    </button>
                  </div>
                  <hr className="border-brand-yellow/20" />
                  {/* Audience Section */}
                  <div>
                    <h2 className="text-2xl font-extrabold mb-5 text-brand-yellow flex items-center gap-2 justify-center tracking-wide">
                      <img src="/images/white-glasses.svg" alt="Join Game" className="h-7" />
                      Join Game
                    </h2>
                    <button
                      onClick={handleJoinAsAudience}
                      className="w-full bg-brand-yellow text-black font-extrabold text-xl py-4 rounded-xl hover:bg-[#fccd6c]/90 transition-colors shadow-lg"
                    >
                      Join as Audience
                    </button>
                  </div>
                  <hr className="border-brand-yellow/20" />
                  <div className="text-center mt-2">
                    <Link to="/admin" className="text-brand-yellow hover:underline text-lg font-semibold">Admin Panel</Link>
                  </div>
                </div>
              </div>
            </div>
          ) : view === 'host-setup' && gameId && gameCode ? (
            <HostSetupScreen
              gameId={gameId}
              gameCode={gameCode}
              players={players}
              onAddPlayer={handleAddPlayer}
              onRemovePlayer={handleRemovePlayer}
              onStartGame={handleStartGame}
              onBack={handleBack}
            />
          ) : (
            <div className="min-h-screen bg-black">
              {view === 'host' && <HostDashboard gameId={gameId} />}
              {view === 'player' && playerId && (
                <GameBoard gameId={gameId} playerId={playerId} />
              )}
              {view === 'audience' && <AudienceView gameId={gameId} playerId={playerId || undefined} />}
            </div>
          )
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
